import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Split, Loader2, ShieldAlert, MailCheck, ArrowLeft, Eye, EyeOff } from "lucide-react";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const navigate = useNavigate();
  const { login, signup, verifyOtp, resendOtp, loginWithGoogle } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [lockout, setLockout] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // OTP step state
  const [otp, setOtp] = useState(null); // { email, reason } once a code has been sent
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(true);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const submit = async (e) => {
    e.preventDefault();
    if (!isLogin && !acceptTerms) {
      toast.error("Please accept the Terms of Service and Privacy Policy to continue.");
      return;
    }
    setBusy(true);
    setLockout("");
    try {
      const res = isLogin
        ? await login(email, password)
        : await signup(email, password, name, acceptTerms);
      if (res?.requires_otp) {
        setOtp({ email: res.email, reason: res.reason });
        setResendIn(45);
        toast.success("We emailed you a 6-digit code.");
        return;
      }
      toast.success(isLogin ? "Welcome back!" : "Account created!");
      navigate(res?.role === "admin" ? "/admin" : "/browse");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429) {
        setLockout(detail || "Too many failed attempts. Please try again in a few minutes.");
      } else {
        toast.error(detail || "Something went wrong");
      }
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await verifyOtp(otp.email, code.trim(), remember);
      toast.success("Verified!");
      navigate(u?.role === "admin" ? "/admin" : "/browse");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (resendIn > 0) return;
    try {
      await resendOtp(otp.email);
      setResendIn(45);
      setCode("");
      toast.success("A new code is on its way.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 429 && d) {
        const m = String(d).match(/(\d+)s/);
        if (m) setResendIn(Number(m[1]));
      }
      toast.error(d || "Couldn't resend the code");
    }
  };

  const otpTitle = otp?.reason === "2fa" ? "Verify it's you" : "Confirm your email";
  const otpSub = otp?.reason === "2fa"
    ? "For your security, enter the 6-digit code we just emailed you."
    : "Enter the 6-digit code we emailed to activate your account.";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* left panel */}
      <div className="hidden lg:flex flex-col justify-between bg-foreground text-background p-12 grain-bg">
        <Link to="/" className="flex items-center gap-2" data-testid="auth-logo">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Split className="h-5 w-5" />
          </div>
          <span className="font-heading text-xl" style={{ fontWeight: 900 }}>BogoShare</span>
        </Link>
        <div>
          <h2 className="font-heading text-4xl leading-tight" style={{ fontWeight: 900 }}>
            Two shoppers.<br />One deal.<br /><span className="text-accent">Half the cost.</span>
          </h2>
          <p className="mt-4 text-background/70 max-w-sm leading-relaxed">
            Join the marketplace where Buy-One-Get-One offers finally make sense.
          </p>
        </div>
        <p className="text-xs font-mono-code text-background/50">Secure code-exchange settlement</p>
      </div>

      {/* form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-sm"
        >
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <Split className="h-5 w-5" />
            </div>
            <span className="font-heading text-xl" style={{ fontWeight: 900 }}>BogoShare</span>
          </div>

          <h1 className="font-heading text-3xl tracking-tight" style={{ fontWeight: 700 }}>
            {otp ? otpTitle : isLogin ? "Log in" : "Create account"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {otp ? otpSub : isLogin ? "Welcome back — let's find your next split." : "Start sharing deals in under a minute."}
          </p>

          {otp ? (
            <form onSubmit={submitCode} className="mt-6 space-y-4" data-testid="otp-form">
              <div className="flex items-center gap-2 rounded-lg border border-black/10 bg-secondary/40 p-3 text-sm">
                <MailCheck className="h-4 w-4 text-primary shrink-0" />
                <span className="text-muted-foreground">Code sent to <b className="text-foreground">{otp.email}</b></span>
              </div>
              <div>
                <Label htmlFor="otp-code">6-digit code</Label>
                <Input
                  id="otp-code" data-testid="otp-code-input" value={code} required autoFocus
                  inputMode="numeric" maxLength={6} placeholder="••••••"
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="mt-1 text-center text-2xl tracking-[0.5em] font-mono-code"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <Checkbox data-testid="otp-remember-checkbox" checked={remember}
                  onCheckedChange={(v) => setRemember(!!v)} />
                <span>Trust this device for 30 days (skip codes when logging in here)</span>
              </label>
              <Button
                type="submit" data-testid="otp-verify-btn" disabled={busy || code.length !== 6}
                className="w-full h-11 rounded-full hard-shadow-sm hover:-translate-y-0.5 transition-transform duration-200"
              >
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Verify & continue
              </Button>
              <div className="flex items-center justify-between text-sm">
                <button type="button" data-testid="otp-back-btn"
                  onClick={() => { setOtp(null); setCode(""); }}
                  className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground">
                  <ArrowLeft className="h-3.5 w-3.5" /> Back
                </button>
                <button type="button" data-testid="otp-resend-btn" onClick={resend} disabled={resendIn > 0}
                  className="font-semibold text-primary hover:underline disabled:opacity-50 disabled:no-underline">
                  {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                </button>
              </div>
            </form>
          ) : (
          <>
          <Button
            type="button"
            variant="outline"
            data-testid="google-login-btn"
            onClick={loginWithGoogle}
            className="mt-6 w-full h-11 rounded-full border-black/20 hover:-translate-y-0.5 transition-transform duration-200"
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
            Continue with Google
          </Button>

          <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" /> OR <div className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {isLogin && lockout && (
              <div
                data-testid="login-lockout-notice"
                role="alert"
                className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700"
              >
                <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{lockout}</span>
              </div>
            )}
            {!isLogin && (
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name" data-testid="auth-name-input" value={name} required
                  onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" className="mt-1"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" type="email" data-testid="auth-email-input" value={email} required
                onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {isLogin && (
                  <Link to="/forgot-password" data-testid="forgot-password-link"
                    className="text-xs font-semibold text-primary hover:underline">
                    Forgot password?
                  </Link>
                )}
              </div>
              <div className="relative mt-1">
                <Input
                  id="password" type={showPassword ? "text" : "password"} data-testid="auth-password-input"
                  value={password} required
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pr-10"
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {!isLogin && <PasswordStrengthMeter password={password} />}
            </div>
            {!isLogin && (
              <label className="flex items-start gap-2 cursor-pointer text-sm text-muted-foreground">
                <Checkbox data-testid="accept-terms-checkbox" checked={acceptTerms}
                  onCheckedChange={(v) => setAcceptTerms(!!v)} className="mt-0.5" />
                <span>
                  I agree to the{" "}
                  <Link to="/terms" target="_blank" data-testid="signup-terms-link" className="font-semibold text-primary hover:underline">Terms of Service</Link>
                  {" "}and{" "}
                  <Link to="/privacy" target="_blank" data-testid="signup-privacy-link" className="font-semibold text-primary hover:underline">Privacy Policy</Link>.
                </span>
              </label>
            )}
            <Button
              type="submit" data-testid="auth-submit-btn" disabled={busy || (!isLogin && !acceptTerms)}
              className="w-full h-11 rounded-full hard-shadow-sm hover:-translate-y-0.5 transition-transform duration-200"
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isLogin ? "Log in" : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground text-center">
            {isLogin ? "New here? " : "Already have an account? "}
            <Link
              to={isLogin ? "/signup" : "/login"}
              data-testid="auth-toggle-link"
              className="font-semibold text-primary hover:underline"
            >
              {isLogin ? "Create an account" : "Log in"}
            </Link>
          </p>
          </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
